/**
 * src/domain/arcade/game-state-contracts.ts
 *
 * Game State Contracts & Merkle State Root Types for the Mesh Arcade (Step 3.2).
 */

export type UnitType = 'SCOUT' | 'HEAVY' | 'TECH';

export type ActionType = 'MOVE' | 'FIRE' | 'DEPLOY_SHIELD' | 'HACK_EMP' | 'STALLED_DEFEND';

export interface GridCoord {
  x: number;
  y: number;
}

export interface TacticalUnit {
  id: string;
  ownerDid: string;
  type: UnitType;
  coord: GridCoord;
  hp: number;
  maxHp: number;
  ap: number;
  maxAp: number;
  isShielded?: boolean;
  isEmpDisabled?: boolean;
}

export interface UnitAction {
  unitId: string;
  type: ActionType;
  targetCoord?: GridCoord;
  costAp: number;
  initiative: number; // Higher executes earlier: HACK_EMP(100) > DEPLOY_SHIELD(80) > MOVE(50) > FIRE(30) > STALLED_DEFEND(10)
}

export interface PlayerActionVector {
  playerDid: string;
  tickNumber: number;
  actions: UnitAction[];
  totalApSpent: number;
}

export interface MerkleGameState {
  matchId: string;
  tickNumber: number;
  units: Record<string, TacticalUnit>;
  gridDimensions: { width: number; height: number };
  stateRoot: string;
  timestamp: number;
}

/**
 * Universal synchronous SHA-256 implementation (works in browser & Node.js).
 */
export function sha256Sync(str: string): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
  const utf8 = unescape(encodeURIComponent(str));
  const words: number[] = [];
  for (let i = 0; i < utf8.length; i++) {
    words[i >> 2] |= (utf8.charCodeAt(i) & 0xff) << (24 - (i % 4) * 8);
  }
  words[utf8.length >> 2] |= 0x80 << (24 - (utf8.length % 4) * 8);
  words[(((utf8.length + 8) >> 6) << 4) + 15] = utf8.length * 8;

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const w = new Array(64);
  for (let i = 0; i < words.length; i += 16) {
    for (let j = 0; j < 16; j++) w[j] = words[i + j] | 0;
    for (let j = 16; j < 64; j++) {
      const s0 = rotr(7, w[j - 15]) ^ rotr(18, w[j - 15]) ^ (w[j - 15] >>> 3);
      const s1 = rotr(17, w[j - 2]) ^ rotr(19, w[j - 2]) ^ (w[j - 2] >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) | 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;

    for (let j = 0; j < 64; j++) {
      const s1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + K[j] + w[j]) | 0;
      const s0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) | 0;

      h = g; g = f; f = e; e = (d + temp1) | 0;
      d = c; c = b; b = a; a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const toHex = (n: number) => ('00000000' + (n >>> 0).toString(16)).slice(-8);
  return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4) + toHex(h5) + toHex(h6) + toHex(h7);
}

/**
 * Computes deterministic Merkle state root for synchronization verification:
 * StateRoot = SHA256(TickNumber + UnitsStateJSON + GridStateJSON)
 */
export function computeStateRoot(
  tickNumber: number,
  units: Record<string, TacticalUnit>,
  gridDimensions: { width: number; height: number }
): string {
  // Sort unit keys for deterministic JSON serialization
  const sortedUnitKeys = Object.keys(units).sort();
  const sortedUnits: Record<string, TacticalUnit> = {};
  for (const k of sortedUnitKeys) {
    sortedUnits[k] = units[k];
  }

  const payload = `${tickNumber}:${JSON.stringify(sortedUnits)}:${JSON.stringify(gridDimensions)}`;
  return sha256Sync(payload);
}
